import { useCallback, useEffect, useState } from 'react';

import { isPropertyFilterDef } from '../DataTable/utils';

import type { SelectItemOption } from '@kc-console/common-ui';
import type { FilterDefBase, FilterOption, FilterState } from '../DataTable/types';

export type UseFilterUtilsParams = {
  filterDef: FilterDefBase | null | undefined;
  filterIndex: number | null | undefined;
  filters: FilterState[];
  defaultValues?: {
    filterKey?: string | null;
  };
};

export const useFilterUtils = (params: UseFilterUtilsParams) => {
  const { filterDef, filterIndex, filters, defaultValues } = params;

  const getFiltersByKeyExcludingCurrent = useCallback(
    (key: string): FilterState[] => {
      return filters.filter((filter, index) => filter.key === key && index !== filterIndex);
    },
    [filters, filterIndex],
  );

  /** 필터키로 정의된 옵션 */
  const filterKeyOptions = filterDef && isPropertyFilterDef(filterDef) ? filterDef.options : [];

  /** 필터키로 선택할 수 있는 옵션 (다중 선택, 중복 등 현재 상태 반영) */
  const filterKeySelectOptions = filterKeyOptions.map(filterKeyOption => {
    const filtersWithSameKeyExcludingCurrent = getFiltersByKeyExcludingCurrent(filterKeyOption.id);
    return {
      value: filterKeyOption.id,
      text: filterKeyOption.label,
      disabled: !filterKeyOption.enableMultiSelect && filtersWithSameKeyExcludingCurrent.length > 0,
    };
  });

  /** 필터값으로 선택할 수 있는 옵션들, 선택된 필터키에 따라 변경됨 */
  const [
    { options: filterValueSelectOptions, isLoading: isFilterValueSelectOptionsLoading },
    setFilterValueSelectOptions,
  ] = useState<{
    isLoading: boolean;
    options: SelectItemOption<unknown>[];
  }>({ options: [], isLoading: false });

  /** 현재 선택된 필터 대상이 있는 경우(필터칩) */
  const prevFilter = !!filterIndex || filterIndex === 0 ? filters.at(filterIndex) : undefined;
  const prevFilterKey = prevFilter?.key ?? null;
  const prevFilterValue = prevFilter?.value ?? null;

  /** 선택된 필터 키 (option.value) */
  const [filterKey, setFilterKey] = useState(() => {
    if (defaultValues?.filterKey !== undefined) return defaultValues.filterKey;
    return prevFilterKey ?? filterKeySelectOptions.at(0)?.value ?? null;
  });
  /** 선택된 필터 값 */
  const [filterValue, setFilterValue] = useState<unknown>(prevFilterValue);

  /** 선택된 필터 키d의 원본 옵션 (option) */
  const filterKeyOption = filterKeyOptions.find(option => option.id === filterKey);

  /** 현재 선택된 필터가 변경되었는지 여부 */
  const isFilterChanged =
    !!prevFilter && (prevFilterKey !== filterKey || prevFilterValue !== filterValue);
  const isFilterDuplicated = filters.some(
    ({ key, value }, index) => index !== filterIndex && key === filterKey && value === filterValue,
  );
  /** 현재 선택된 필터의 값이 지정되지 않았는지 여부 */
  const isFilterValueEmpty =
    filterValue === '' || filterValue === null || filterValue === undefined;
  /** 필터 값의 자동완성을 위해 사용하는 키워드(현재 입력된 필터 값) */
  const filterValueSearch = typeof filterValue === 'string' ? filterValue : '';

  /** 필터 값으로 선택할 수 있는 옵션을 업데이트 하는 함수(다중 선택, 중복, 현재 다른 필터 상태 반영)) */
  const updateFilterValueOptions = useCallback(
    async (options: FilterOption[] | PromiseLike<FilterOption[]>) => {
      const filterValueOptions = (await Promise.resolve(options)).map(option => {
        const filtersWithSameKeyExcludingCurrent = filterKeyOption
          ? getFiltersByKeyExcludingCurrent(filterKeyOption.id)
          : [];
        return {
          value: option.value,
          text: option.label,
          disabled:
            filterKeyOption &&
            filtersWithSameKeyExcludingCurrent.some(filter => filter.value === option.value),
        };
      });
      setFilterValueSelectOptions({ options: filterValueOptions, isLoading: false });
    },
    [filterKeyOption, getFiltersByKeyExcludingCurrent],
  );

  useEffect(() => {
    // 필터 추가/변경/제거 또는 선택된 필터키 변경 시, 자동완성 갱신
    if (filterKeyOption?.type === 'select') {
      updateFilterValueOptions(filterKeyOption.options);
    } else if (filterKeyOption?.type === 'text' && filterKeyOption.autoCompleteFn) {
      const autoCompleteFn = filterKeyOption.autoCompleteFn;
      setFilterValueSelectOptions(prev => ({ ...prev, isLoading: true }));
      updateFilterValueOptions(autoCompleteFn(filterValueSearch));
    } else {
      updateFilterValueOptions([]);
    }
  }, [filters, filterKeyOption]);

  useEffect(() => {
    // value 입력 시, 자동완성 필터링 갱신 (디바운스)
    if (filterKeyOption?.type === 'text' && filterKeyOption.autoCompleteFn) {
      const autoCompleteFn = filterKeyOption.autoCompleteFn;
      setFilterValueSelectOptions(prev => ({ ...prev, isLoading: true }));
      const timer = window.setTimeout(() => {
        updateFilterValueOptions(autoCompleteFn(filterValueSearch));
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [filterValueSearch]);

  /** 필터 키 변경 함수 */
  const handleFilterKeyChange = (key: string | null) => {
    if (filterKey === key) return;
    setFilterKey(key);
    setFilterValue(filterKeySelectOptions ? null : ''); // 필터 키 변경 시 필터 값 초기화
  };

  return {
    filterKey,
    onFilterKeyChange: handleFilterKeyChange,
    filterKeyOptions,
    filterKeyOption,
    filterKeySelectOptions,
    filterValue,
    onFilterValueChange: setFilterValue,
    filterValueSelectOptions,
    isFilterValueSelectOptionsLoading,
    isFilterChanged,
    isFilterDuplicated,
    isFilterValueEmpty,
  };
};
